import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import client from "@/lib/api";

const AuthContext = createContext(null);

// Session policy: 30-minute tokens. While the user is ACTIVE (mouse/keys/touch)
// the token is silently renewed, so working sessions never expire. After ~30
// minutes with NO activity the token lapses server-side and the next action
// returns to the sign-in screen (CJIS-style idle session lock).
const REFRESH_EVERY_MS = 4 * 60 * 1000;   // renew attempt cadence
const ACTIVE_WINDOW_MS = 5 * 60 * 1000;   // only renew if input seen this recently
const IDLE_LIMIT_MS = 30 * 60 * 1000;     // matches backend token life

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=auth
  const [loading, setLoading] = useState(true);
  const lastActivity = useRef(Date.now());

  const doRefresh = useCallback(async () => {
    try {
      const { data } = await client.post("/auth/refresh");
      if (data.token) localStorage.setItem("sentinel_token", data.token);
    } catch { /* transient errors ignored; a true 401 is handled by the api interceptor */ }
  }, []);

  // Manual "stay signed in" (idle-warning banner): counts as activity + renews now.
  const keepAlive = useCallback(() => {
    lastActivity.current = Date.now();
    doRefresh();
  }, [doRefresh]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Activity-gated sliding refresh: keep active shifts alive indefinitely,
  // let idle terminals lapse after ~30 minutes of no input.
  useEffect(() => {
    if (!user) return;
    const mark = () => { lastActivity.current = Date.now(); };
    const evs = ["mousedown", "keydown", "touchstart", "mousemove", "wheel"];
    evs.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    const iv = setInterval(() => {
      if (Date.now() - lastActivity.current < ACTIVE_WINDOW_MS) doRefresh();
    }, REFRESH_EVERY_MS);
    // Waking a sleeping tab: renew immediately if the session hasn't lapsed yet.
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() - lastActivity.current < IDLE_LIMIT_MS) {
        mark();
        doRefresh();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    doRefresh(); // rotate right away on mount so cookie + localStorage agree
    return () => {
      clearInterval(iv);
      evs.forEach((e) => window.removeEventListener(e, mark));
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user, doRefresh]);

  const login = async (email, password, acknowledged) => {
    const { data } = await client.post("/auth/login", { email, password, acknowledged });
    if (data.token) localStorage.setItem("sentinel_token", data.token);
    setUser(data);
    return data;
  };

  const pinLogin = async (pin, acknowledged) => {
    const { data } = await client.post("/auth/pin-login", { pin, acknowledged });
    if (data.token) localStorage.setItem("sentinel_token", data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await client.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("sentinel_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, pinLogin, logout, lastActivityRef: lastActivity, keepAlive }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
